import type { SolutePreset } from './solutionsLab';
import { SOLUBILITY_GRAPH_MAX_G_PER_100G } from './solutionsExplore';

/**
 * Solubility curve with the learner's load (g solute per 100 g water) as a dashed line, a yellow
 * marker at the hot temperature and a green marker at the cooled temperature. With `limits`, open
 * rings mark the curve value (the most that can dissolve) at each temperature.
 */
export function SolubilityGraph({
  preset,
  hotTemperature,
  coolTemperature,
  loadPer100g,
  limits,
}: {
  preset: SolutePreset;
  hotTemperature: number;
  coolTemperature: number;
  loadPer100g: number;
  limits?: { hot: number; cool: number };
}) {
  const x = (temperature: number) => 34 + (temperature / 100) * 286;
  const y = (grams: number) => 176 - (Math.min(SOLUBILITY_GRAPH_MAX_G_PER_100G, grams) / SOLUBILITY_GRAPH_MAX_G_PER_100G) * 148;
  const points = preset.points
    .map((point) => `${x(point.temperatureC)},${y(point.gramsPer100gWater)}`)
    .join(' ');
  const load = Math.round(loadPer100g * 10) / 10;
  return (
    <svg
      viewBox="0 0 340 200"
      className="w-full"
      role="img"
      aria-label={`${preset.name} solubility curve with ${load} grams per 100 grams of water added`}
    >
      <path d="M34 20V176H325" fill="none" stroke="currentColor" className="text-slate2" />
      {[0, 50, 100, 150, 200, 250].map((tick) => (
        <g key={tick}>
          <path
            d={`M34 ${y(tick)}H325`}
            stroke="currentColor"
            className="text-hairline"
            strokeDasharray="3 5"
          />
          <text x="2" y={y(tick) + 4} className="fill-slate2 text-[9px] font-bold">
            {tick}
          </text>
        </g>
      ))}
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={preset.colour === 'coral' ? 'text-brand-coral' : 'text-brand-sky'}
      />
      <path
        d={`M34 ${y(loadPer100g)}H325`}
        stroke="currentColor"
        strokeWidth="3"
        strokeDasharray="8 5"
        className="text-ink"
        data-testid="solubility-load-line"
      />
      {limits ? (
        <>
          <circle cx={x(hotTemperature)} cy={y(limits.hot)} r="6" fill="none" strokeWidth="2.5" className="stroke-brand-sunshine" />
          <circle cx={x(coolTemperature)} cy={y(limits.cool)} r="6" fill="none" strokeWidth="2.5" className="stroke-brand-mint" />
        </>
      ) : null}
      <circle cx={x(hotTemperature)} cy={y(loadPer100g)} r="7" className="fill-brand-sunshine" data-testid="solubility-hot-marker" />
      <circle cx={x(coolTemperature)} cy={y(loadPer100g)} r="7" className="fill-brand-mint" data-testid="solubility-cool-marker" />
      <text x="36" y="195" className="fill-slate2 text-[10px] font-bold">
        Temperature (°C)
      </text>
      <text x="40" y="14" className="fill-slate2 text-[10px] font-bold">
        g solute / 100 g water
      </text>
    </svg>
  );
}
